/*───────────────────────────────────────────────────────────────
 📄 File: IVRCallDetailsSection.tsx
 🧩 Description:
    Expandable "Call Details" panel for IVRStatsPage.
    Shows action-type filter, paginated table with records,
    and export-to-CSV button.
────────────────────────────────────────────────────────────────*/

import { List, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import type { IVRStatRecord } from '../types';

interface IVRCallDetailsSectionProps {
    showDetails: boolean;
    onToggleDetails: () => void;
    detailsTotal: number;
    detailsLoading: boolean;
    callDetails: IVRStatRecord[];
    actionFilter: string;
    onActionFilterChange: (value: string) => void;
    detailsPage: number;
    detailsLimit: number;
    onPageChange: (page: number) => void;
    onExport: () => void;
    t: (key: string, fallback?: string) => string;
}

function ActionTypeBadge({ type }: { type: string }) {
    const colorMap: Record<string, string> = {
        option_pressed: 'bg-green-100 text-green-800',
        timeout: 'bg-yellow-100 text-yellow-800',
        failure: 'bg-red-100 text-red-800',
        invalid: 'bg-orange-100 text-orange-800',
        direct_dial: 'bg-blue-100 text-blue-800',
    };
    return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${colorMap[type] ?? 'bg-gray-100 text-gray-800'}`}>
            {type.replace('_', ' ')}
        </span>
    );
}

export function IVRCallDetailsSection({
    showDetails,
    onToggleDetails,
    detailsTotal,
    detailsLoading,
    callDetails,
    actionFilter,
    onActionFilterChange,
    detailsPage,
    detailsLimit,
    onPageChange,
    onExport,
    t,
}: IVRCallDetailsSectionProps) {
    return (
        <FormBox title={t('ivrStats:sections.callDetails', 'Call Details')} icon={<List className="h-5 w-5" />}>
            {/* Toggle Header */}
            <div
                className="flex items-center gap-3 cursor-pointer mb-4 -mt-2"
                onClick={onToggleDetails}
            >
                <span className="text-sm text-muted-foreground">
                    ({detailsTotal.toLocaleString()} {t('ivrStats:labels.records', 'records')})
                </span>
                {showDetails ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">
                    {showDetails ? 'Click to collapse' : 'Click to expand'}
                </span>
            </div>

            {showDetails && (
                <div className="space-y-4">
                    {/* Filter + Export */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Label className="text-sm">{t('ivrStats:filters.actionType', 'Action Type')}:</Label>
                            <select
                                value={actionFilter}
                                onChange={(e) => { onActionFilterChange(e.target.value); onPageChange(0); }}
                                className="px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                <option value="all">{t('ivrStats:filters.all', 'All')}</option>
                                <option value="option_pressed">{t('ivrStats:filters.optionPressed', 'Option Pressed')}</option>
                                <option value="timeout">{t('ivrStats:filters.timeout', 'Timeout')}</option>
                                <option value="failure">{t('ivrStats:filters.failure', 'Failure')}</option>
                                <option value="invalid">{t('ivrStats:filters.invalid', 'Invalid')}</option>
                                <option value="direct_dial">{t('ivrStats:filters.directDial', 'Direct Dial')}</option>
                                <option value="exit">{t('ivrStats:filters.exit', 'Exit')}</option>
                            </select>
                        </div>
                        {callDetails.length > 0 && (
                            <Button onClick={onExport} variant="outline" size="sm">
                                <Download className="h-4 w-4 mr-2" />
                                {t('ivrStats:actions.exportDetails', 'Export Details')}
                            </Button>
                        )}
                    </div>

                    {/* Table or Loading or Empty */}
                    {detailsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        </div>
                    ) : callDetails.length > 0 ? (
                        <>
                            <div className="overflow-x-auto rounded-lg border">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50 border-b">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold">{t('ivrStats:table.dateTime', 'Date/Time')}</th>
                                            <th className="px-4 py-3 text-left font-semibold">{t('ivrStats:table.callerId', 'Caller ID')}</th>
                                            <th className="px-4 py-3 text-left font-semibold">{t('ivrStats:table.action', 'Action')}</th>
                                            <th className="px-4 py-3 text-left font-semibold">{t('ivrStats:table.digit', 'Digit')}</th>
                                            <th className="px-4 py-3 text-left font-semibold">{t('ivrStats:table.destination', 'Destination')}</th>
                                            <th className="px-4 py-3 text-right font-semibold">{t('ivrStats:table.waitTime', 'Wait (s)')}</th>
                                            <th className="px-4 py-3 text-right font-semibold">{t('ivrStats:table.duration', 'Duration (s)')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {callDetails.map((record) => (
                                            <tr key={record.id} className="hover:bg-muted/30 transition-colors">
                                                <td className="px-4 py-3 whitespace-nowrap">{new Date(record.createdAt).toLocaleString()}</td>
                                                <td className="px-4 py-3">
                                                    <div className="font-mono text-xs">{record.callerIdNumber || '-'}</div>
                                                    {record.callerIdName && <div className="text-xs text-muted-foreground">{record.callerIdName}</div>}
                                                </td>
                                                <td className="px-4 py-3"><ActionTypeBadge type={record.actionType} /></td>
                                                <td className="px-4 py-3 font-mono text-center">{record.digitPressed || '-'}</td>
                                                <td className="px-4 py-3 text-xs font-mono">{record.optionDestination || '-'}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{record.waitTime !== null ? record.waitTime.toFixed(1) : '-'}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{record.totalDuration !== null ? record.totalDuration.toFixed(1) : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-muted-foreground">
                                    {t('ivrStats:table.showing', 'Showing')} {detailsPage * detailsLimit + 1} - {Math.min((detailsPage + 1) * detailsLimit, detailsTotal)} of {detailsTotal.toLocaleString()}
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={() => onPageChange(Math.max(0, detailsPage - 1))} disabled={detailsPage === 0} variant="outline" size="sm">
                                        {t('ivrStats:table.previous', 'Previous')}
                                    </Button>
                                    <Button onClick={() => onPageChange(detailsPage + 1)} disabled={(detailsPage + 1) * detailsLimit >= detailsTotal} variant="outline" size="sm">
                                        {t('ivrStats:table.next', 'Next')}
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-muted-foreground">{t('ivrStats:messages.noCallDetails', 'No call details found for the selected filters')}</p>
                        </div>
                    )}
                </div>
            )}
        </FormBox>
    );
}
